import React, { Component } from 'react';
import { isNil } from 'lodash';
import { classes } from 'typestyle';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { getStyleAttributes } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.client';
import { CC_COLOR, ISASSORTMENT } from 'src/utils/Domain/Constants';
import { AssortmentClient } from 'src/pages/AssortmentBuild/Assortment.client';
import {
  SectionColorHeader,
  createSectionElement,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSections/StyleEditSections.utils';
import { StyleEditViewSection } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.types';

import styleEditStyles from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.styles';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { generateCart } from '../../AssortmentAdd/Assortment.client';
import { connect } from 'react-redux';
import type { AppState } from 'src/reducers';

type StyleEditSectionsProps = {
  sectionsConfig: StyleEditViewSection[];
  styleData?: BasicPivotItem;
  allStyleColors: BasicPivotItem[];
  filteredStyleColors: BasicPivotItem[];
  existingStyleColors: BasicPivotItem[];
  sortedStyleColorIds: string[];
  removableStyleColors: string[];
  showOnlyPreexist?: boolean;
  /**
   * This callback is used by the children to tell sections to increment it's state.currentRefreshCount
   * Which is the signal for all child sections to refresh their data
   */
  onRefresh: (refreshCompanion?: boolean) => void;
  /**
   * This is used to track children sections requesting cache clearing and data re-fetching to all sections
   */
  onSectionChange: (isExpanded: boolean, sectionDataLoaded: boolean, isGrid: boolean) => void;
};

type StyleEditSectionsState = {
  currentPanel: string | null;
  sectionDataLoaded: boolean;
  /**
   * @deprecated
   */
  stickyHeaderAdditionElement?: JSX.Element;
  /**
   * This is used to track children sections requesting cache clearing and data re-fetching to all sections
   */
  currentRefreshCount: number;
  lastEditedSection: string | null;
};

type StyleEditSectionsRedux = {
  placeholderImgAttr?: string;
};
export class StyleEditSections extends Component<
  StyleEditSectionsProps & StyleEditSectionsRedux,
  StyleEditSectionsState
> {
  constructor(props: StyleEditSectionsProps) {
    super(props);

    this.state = {
      currentPanel: null,
      sectionDataLoaded: false,
      currentRefreshCount: 0,
      lastEditedSection: null,
    };
  }

  getExistingChild = (colorId: string) => {
    const { styleData, existingStyleColors } = this.props;
    if (isNil(styleData)) {
      return null;
    }

    return existingStyleColors.find((child) => child[CC_COLOR] === colorId);
  };

  onRefetchData = () => {
    this.refreshSections(null);
  };

  refreshSections = (componentId: string | null) => {
    // Adding a componentId makes styleEdit not refresh that section
    this.setState({ currentRefreshCount: this.state.currentRefreshCount + 1, lastEditedSection: componentId || null });
  };

  addColor = async (selectedNewColor: string, copyFrom: BasicPivotItem): Promise<void> => {
    const { styleData, placeholderImgAttr } = this.props;

    if (isNil(styleData)) {
      return;
    }

    let type: 'existing' | 'similar';
    let toCopy: BasicPivotItem;
    const existingChild = this.getExistingChild(selectedNewColor);

    if (!isNil(existingChild)) {
      toCopy = existingChild;
      type = 'existing';
    } else {
      toCopy = await getStyleAttributes(copyFrom.id);
      type = 'similar';
    }

    const newItem = {
      ...toCopy,
      name: `${styleData.name}: ${selectedNewColor}`,
      description: `${styleData.description} ${selectedNewColor}`,
      colorId: selectedNewColor,
      [ISASSORTMENT]: 'false',
      [CC_COLOR]: selectedNewColor,
      type,
    };

    const toSend = {
      ...styleData,
      type: 'existing',
      children: [
        {
          ...newItem,
        },
      ],
    };
    const cartInfo = await generateCart();
    await AssortmentClient.addItemsToAssortment({ items: [toSend], placeholderImgAttr, cart_id: cartInfo.cart_id });
    this.props.onRefresh(); // TODO: check this behaves the same as below
    // await this.getData();
  };

  handlePanelChange(newPanel: string, isGrid: boolean) {
    const lastPanel = this.state.currentPanel;
    const panelClosing = lastPanel === newPanel;
    const panel = panelClosing ? null : newPanel;

    const isExpanded = !isNil(panel);
    this.props.onSectionChange(isExpanded, panelClosing, isGrid);

    this.setState({
      currentPanel: panel,
      sectionDataLoaded: panelClosing,
    });
  }

  render() {
    const { sectionsConfig, styleData } = this.props;
    if (isNil(styleData)) {
      return <div />;
    }

    const { currentPanel, stickyHeaderAdditionElement } = this.state;

    return sectionsConfig.map((sectionConfig, index) => {
      const panelKey = `stylecolor_panel${index}_${sectionConfig.text}`;
      const expanded = currentPanel === panelKey;
      const isColorHeaderNeeded = sectionConfig.componentType === 'column_multi_grid';
      const isDummySectionNeeded =
        sectionConfig.componentType === 'column_grid' || sectionConfig.componentType === 'column_grid_with_button';
      const isGrid =
        sectionConfig.componentType === 'column_grid' ||
        sectionConfig.componentType === 'column_multi_grid' ||
        sectionConfig.componentType === 'column_grid_with_button';
      return (
        <Accordion
          data-qa={'StyleEditSectionPanel'}
          data-qa-key={sectionConfig.text}
          expanded={expanded}
          onChange={() => this.handlePanelChange(panelKey, isGrid)}
          key={panelKey}
          elevation={2}
          TransitionProps={{ timeout: 0 }}
        >
          <AccordionSummary
            expandIcon={<i className={`fas fa-angle-down`} />}
            className={classes(
              expanded
                ? classes(styleEditStyles.stickySection, styleEditStyles.stickyHeaderArrowExpanded)
                : styleEditStyles.stickyHeaderArrow,
            )}
          >
            <span className={styleEditStyles.sectionText}>{sectionConfig.text}</span>
            {/* Adds a white box to cover sticky column on scroll */}
            {isDummySectionNeeded && expanded && (
              <div
                style={{
                  height: 100,
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: 60,
                  width: 'calc(100% - 24px)',
                  zIndex: 20,
                  // match ag-grid's border color
                  // borderRight: `1px solid rgb(222, 225, 230)`,
                }}
              >
                <div
                  style={{
                    backgroundColor: 'white',
                    cursor: 'initial',
                    height: '100%',
                    width: 200,
                    position: 'sticky',
                    left: 0,
                  }}
                />
              </div>
            )}
            {expanded && isColorHeaderNeeded && <SectionColorHeader stickyElement={stickyHeaderAdditionElement} />}
          </AccordionSummary>
          <AccordionDetails
            className={classes(
              styleEditStyles.scrollableSectionDetails,
              expanded && isColorHeaderNeeded ? styleEditStyles.scrollableSectionDetailsExpanded : '',
            )}
          >
            {createSectionElement(
              this,
              sectionConfig,
              styleData,
              expanded,
              this.refreshSections,
              this.state.currentRefreshCount,
              this.state.lastEditedSection,
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  }
}

const InternalStyleEditSections = makeScopeAndFilterSensitive(StyleEditSections);

export default connect((state: AppState) => {
  return {
    placeholderImgAttr: state.appConfig.tenantConfig.addBehavior?.placeholderImgAttr,
  };
})(InternalStyleEditSections);
